import type { ReactNode } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import { BadmintonMarkIcon } from "@bw/material";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { visuallyHidden } from "../../../common/visuallyHidden";
import { notificationApi } from "@bw/shared/api";
import type { NotificationResponse } from "@bw/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const diffSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSeconds < 60) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Today / This week / Earlier, by the viewer's local calendar day. */
function groupOf(dateString: string): "Today" | "This week" | "Earlier" {
  const date = new Date(dateString);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  if (date >= startOfToday) return "Today";
  const weekAgo = new Date(startOfToday);
  weekAgo.setDate(weekAgo.getDate() - 6);
  return date >= weekAgo ? "This week" : "Earlier";
}

/** A glyph for the notification's source, by its server type. */
function iconFor(type: string | null | undefined): ReactNode {
  const t = (type ?? "").toLowerCase();
  if (/payment|refund|charge|settlement/.test(t)) return <PaymentsRoundedIcon fontSize="small" />;
  if (/message|inquiry/.test(t)) return <ChatRoundedIcon fontSize="small" />;
  if (/friend|follow/.test(t)) return <PersonAddRoundedIcon fontSize="small" />;
  if (/tournament|league|match|ranking/.test(t)) return <EmojiEventsRoundedIcon fontSize="small" />;
  if (/community|game_night|post/.test(t)) return <GroupsRoundedIcon fontSize="small" />;
  if (/queue|dropin|drop_in|court/.test(t)) return <BadmintonMarkIcon fontSize="small" />;
  if (/program|class|coach|lesson/.test(t)) return <SchoolRoundedIcon fontSize="small" />;
  if (/booking|reservation|membership/.test(t)) return <EventAvailableRoundedIcon fontSize="small" />;
  return <NotificationsNoneRoundedIcon fontSize="small" />;
}

export function PlayerNotificationList({
  pageSize = 20,
  onNavigate,
}: {
  pageSize?: number;
  onNavigate: (actionUrl: string) => void;
}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const query = useQuery({
    queryKey: ["notifications", "list", { page, pageSize, audience: "player" }],
    queryFn: () =>
      notificationApi.list({
        limit: pageSize,
        offset: page * pageSize,
        audience: "player",
      }),
  });
  const markRead = useMutation({
    mutationFn: (notificationId: string) =>
      notificationApi.markRead(notificationId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markAllRead = useMutation({
    mutationFn: () => notificationApi.markAllRead("player"),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  if (query.isLoading) {
    return (
      <Box role="status" aria-label="Loading notifications">
        {Array.from({ length: 5 }, (_, index) => (
          <Stack key={index} direction="row" spacing={1.5} sx={{ px: 2, py: 1.5, alignItems: "center" }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Stack sx={{ flex: 1 }}>
              <Skeleton width="68%" />
              <Skeleton width="42%" />
            </Stack>
          </Stack>
        ))}
      </Box>
    );
  }
  if (query.isError) {
    return (
      <Alert severity="error" role="alert">
        Failed to load notifications. Please try again.
      </Alert>
    );
  }

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const hasUnread = items.some((notification) => !notification.is_read);

  if (items.length === 0) {
    return (
      <Stack spacing={0.5} sx={{ py: 5, alignItems: "center", textAlign: "center" }}>
        <NotificationsNoneRoundedIcon color="action" sx={{ fontSize: 40 }} />
        <Typography component="h2" sx={{ fontWeight: 700 }}>
          All caught up!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No notifications yet. We will let you know when something happens.
        </Typography>
      </Stack>
    );
  }

  const groups = ["Today", "This week", "Earlier"] as const;
  return (
    <Stack spacing={1.5}>
      {hasUnread ? (
        <Stack direction="row" sx={{ justifyContent: "flex-end", mt: -1 }}>
          <Button
            type="button"
            size="small"
            disabled={markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Mark all as read
          </Button>
        </Stack>
      ) : null}
      {groups.map((group) => {
        const groupItems = items.filter((item) => groupOf(item.created_at) === group);
        if (!groupItems.length) return null;
        const headingId = `notifications-${group.replace(" ", "-").toLowerCase()}`;
        return (
          <Stack key={group} component="section" aria-labelledby={headingId} spacing={0.25}>
            <SectionHeader id={headingId} title={group} />
            <List disablePadding>
              {groupItems.map((notification) => (
                <PlayerNotificationItem
                  key={notification.notification_id}
                  notification={notification}
                  onNavigate={onNavigate}
                  onMarkRead={(id) => markRead.mutate(id)}
                />
              ))}
            </List>
          </Stack>
        );
      })}
      {totalPages > 1 ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between" }}>
          <Button
            type="button"
            size="small"
            disabled={page === 0}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            Previous
          </Button>
          <Typography variant="caption" color="text.secondary">
            Page {page + 1} of {totalPages}
          </Typography>
          <Button
            type="button"
            size="small"
            disabled={page >= totalPages - 1}
            onClick={() =>
              setPage((current) => Math.min(totalPages - 1, current + 1))
            }
          >
            Next
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}

function PlayerNotificationItem({
  notification,
  onNavigate,
  onMarkRead,
}: {
  notification: NotificationResponse;
  onNavigate: (actionUrl: string) => void;
  onMarkRead: (notificationId: string) => void;
}) {
  const actionUrl = notification.action_url;
  const actionable = Boolean(actionUrl?.trim());
  const age = formatRelativeTime(notification.created_at);
  const content = (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: notification.is_read ? 500 : 700, overflowWrap: "anywhere" }}>
        {notification.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
      }}>
        {notification.body ? <><span>{notification.body}</span>{" · "}</> : null}
        <time dateTime={notification.created_at}>
          {age}
          {/^\d+[mhd]$/.test(age) ? <Box component="span" sx={visuallyHidden}> ago</Box> : null}
        </time>
      </Typography>
    </Box>
  );
  const rowSx = { gap: 1.5, px: { xs: 0.5, sm: 1 }, py: 1, borderRadius: 2, alignItems: "center", pr: notification.is_read ? 1 : 6 } as const;

  return (
    <ListItem
      disablePadding
      secondaryAction={
        !notification.is_read ? (
          <IconButton
            type="button"
            size="small"
            aria-label="Mark as read"
            title="Mark as read"
            onClick={() => onMarkRead(notification.notification_id)}
          >
            <CheckRoundedIcon fontSize="small" />
          </IconButton>
        ) : null
      }
    >
      {actionable ? (
        <ListItemButton
          onClick={() => {
            onNavigate(actionUrl!);
            onMarkRead(notification.notification_id);
          }}
          sx={rowSx}
        >
          <NotificationIcon type={notification.notification_type} unread={!notification.is_read} />
          {content}
        </ListItemButton>
      ) : (
        <Stack direction="row" sx={{ ...rowSx, width: "100%" }}>
          <NotificationIcon type={notification.notification_type} unread={!notification.is_read} />
          {content}
        </Stack>
      )}
    </ListItem>
  );
}

function NotificationIcon({ type, unread }: { type?: string | null; unread: boolean }) {
  return (
    <Badge color="primary" variant="dot" invisible={!unread} overlap="circular">
      <Box aria-hidden sx={{ width: 40, height: 40, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "action.hover", flexShrink: 0 }}>
        {iconFor(type)}
      </Box>
    </Badge>
  );
}
